import { useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { registrationService } from '@/services/registration.service';
import { Company } from '@/types/registration.types';
import './CompanyStep.scss';
import { Loader } from '../../Loader/Loader';

export interface CompanyFormData {
  companyName: string;
  position: string;
  companyPhone: string;
  companySize: string;
  companyStreet: string;
  unit?: string;
  postalCode?: string;
  city?: string;
  province?: string;
  country?: string;
  remoteObjectId: string;
}

export interface CompanyStepRef {
  submitForm: () => boolean;
  isValid: () => boolean;
  getFormData: () => CompanyFormData;
}

interface CompanyStepProps {
  onNext: SubmitHandler<CompanyFormData>;
  initialData?: Partial<CompanyFormData>;
}

export const CompanyStep = forwardRef<CompanyStepRef, CompanyStepProps>(({ initialData }, ref) => {
  const { t } = useTranslation('auth');
  const { register, setValue, formState: { errors }, getValues } = useForm<CompanyFormData>({
    defaultValues: initialData,
    mode: 'onChange' // Enable validation on change
  });
  
  // Custom validation patterns
  const postalCodePattern = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/; // Canadian postal code pattern
  
  const [companyNotFound, setCompanyNotFound] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companySelectionError, setCompanySelectionError] = useState<string | null>(null);

  // Format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format based on length
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue('companyPhone', formatted);
  };

  // Fetch companies from API
  const { data: companies = [], isLoading: isLoadingCompanies } = useQuery({
    queryKey: ['companies'],
    queryFn: registrationService.getCompanies
  });

  // We'll use the full list of companies instead of filtering
  // This ensures the dropdown always shows all available companies
  const filteredCompanies = companies;

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      // Check if company is selected or 'company not found' is toggled
      if (!selectedCompany && !companyNotFound) {
        setCompanySelectionError('registration.error.companyRequired');
        return false;
      }
      
      // Clear any previous error
      setCompanySelectionError(null);
      
      // Check if there are any form validation errors
      if (Object.keys(errors).length > 0) {
        return false;
      }
      
      return true;
    },
    isValid: () => {
      return Object.keys(errors).length === 0 && (!companySelectionError || companyNotFound);
    },
    getFormData: () => {
      return getValues();
    }
  }));

  // Show loading state
  if (isLoadingCompanies) {
    return <Loader variant="overlay" />;;
  }

  // Handle company selection
  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
    setCompanySelectionError(null);

    // Auto-fill form fields with available company data
    setValue('companyName', company.name);
    setValue('remoteObjectId', company.remoteObjectId);
    setValue('companyPhone', formatPhoneNumber(company.phone || ''));
    setValue('companyStreet', company.street || '');
    setValue('unit', company.unit || '');
    setValue('postalCode', company.postalCode || '');
    setValue('city', company.city || '');
    setValue('province', company.province || '');
    setValue('country', company.country || 'Canada');
  };

  // Toggle company not found
  const handleCompanyNotFoundToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyNotFound(e.target.checked);
    if (e.target.checked) {
      setSelectedCompany(null);
      setValue('companyName', '');
      setValue('companyPhone', '');
      setValue('companyStreet', '');
    }
  };

  return (
    <div className="company-step">
      <div className="company-step__header">
        <h2 className="company-step__title">{t('registration.company.title')}</h2>
        <p className="company-step__description">
          {t('registration.company.description')}
        </p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="company-step__form">
        <div className="company-step__company-selector">
          <div className="company-step__company-field form-field">
            <label className="form-field__label" htmlFor="company">
              {t('registration.company.selectCompany')}
            </label>
            <div className="form-field__select-wrapper">
              <select
                id="company"
                className="form-field__select company-dropdown"
                disabled={companyNotFound}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const company = companies.find((company) => company.id.toString() === selectedId);
                  if (company) {
                    handleCompanySelect(company);
                  } else {
                    // Reset selection if no company is found
                    setSelectedCompany(null);
                  }
                }}
                value={selectedCompany?.id || ''}
              >
                <option value="">{t('registration.company.searchPlaceholder')}</option>
                {filteredCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            {companySelectionError && !companyNotFound && !selectedCompany && (
              <span className="form-field__error">{t(companySelectionError)}</span>
            )}
          </div>
          
          <div className="company-step__toggle-container">
            <span className="company-step__toggle-label">{t('registration.company.notFound')}</span>
            <label className="toggle-switch">
              <input 
                type="checkbox" 
                checked={companyNotFound}
                onChange={handleCompanyNotFoundToggle}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        
        {companyNotFound && (
          <>
            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label" htmlFor="companyName">
                  {t('registration.company.companyName')}
                </label>
                <input
                  type="text"
                  id="companyName"
                  className="form-field__input"
                  placeholder={t('registration.company.searchPlaceholder')}
                  {...register('companyName', { required: true })}
                />
                {errors.companyName && (
                  <span className="form-field__error">{t('validation.required')}</span>
                )}
              </div>

              <div className="form-field">
                <label className="form-field__label" htmlFor="companyPhone">
                  {t('registration.company.phone')}
                </label>
                <input
                  type="tel"
                  id="companyPhone"
                  className="form-field__input"
                  {...register('companyPhone', { 
                    required: true,
                    onChange: handlePhoneChange
                  })}
                  placeholder="(xxx) xxx-xxxx"
                />
                {errors.companyPhone && (
                  <span className="form-field__error">{t('validation.required')}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label" htmlFor="companyStreet">
                  {t('registration.company.street')}
                </label>
                <input
                  type="text"
                  id="companyStreet"
                  className="form-field__input"
                  {...register('companyStreet', { required: true })}
                />
                {errors.companyStreet && (
                  <span className="form-field__error">{t('validation.required')}</span>
                )}
              </div>
              
              <div className="form-field">
                <label className="form-field__label" htmlFor="unit">
                  {t('registration.company.unit')}
                </label>
                <input
                  type="text"
                  id="unit"
                  className="form-field__input"
                  {...register('unit')}
                />
              </div>
            </div>



            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label" htmlFor="postalCode">
                  {t('registration.company.postalCode')}
                </label>
                <input
                  type="text"
                  id="postalCode"
                  className="form-field__input"
                  {...register('postalCode', { 
                    pattern: {
                      value: postalCodePattern,
                      message: 'registration.error.postalCode'
                    }
                  })}
                />
                {errors.postalCode && (
                  <span className="form-field__error">{t(errors.postalCode.message as string)}</span>
                )}
              </div>

              <div className="form-field">
                <label className="form-field__label" htmlFor="city">
                  {t('registration.company.city')}
                </label>
                <input
                  type="text"
                  id="city"
                  className="form-field__input"
                  placeholder={''}
                  {...register('city')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label" htmlFor="province">
                  {t('registration.company.province')}
                </label>
                <div className="form-field__select-wrapper">
                  <select
                    id="province"
                    className="form-field__select"
                    {...register('province')}
                  >
                    <option value="">{t('registration.company.selectProvince')}</option>
                    <option value="Ontario">Ontario</option>
                    <option value="Quebec">Quebec</option>
                    <option value="British Columbia">British Columbia</option>
                    <option value="Alberta">Alberta</option>
                    <option value="Manitoba">Manitoba</option>
                    <option value="Saskatchewan">Saskatchewan</option>
                    <option value="Nova Scotia">Nova Scotia</option>
                    <option value="New Brunswick">New Brunswick</option>
                    <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                    <option value="Prince Edward Island">Prince Edward Island</option>
                    <option value="Yukon">Yukon</option>
                    <option value="Northwest Territories">Northwest Territories</option>
                    <option value="Nunavut">Nunavut</option>
                  </select>
                </div>
              </div>

              <div className="form-field">
                <label className="form-field__label" htmlFor="country">
                  {t('registration.company.country')}
                </label>
                <div className="form-field__input-wrapper">
                  <input
                    type="text"
                    id="country"
                    className="form-field__input"
                    value="Canada"
                    disabled
                    readOnly
                    {...register('country', { value: 'Canada' })}
                  />
                </div>
              </div>
            </div>
          </>
        )}


      </form>
    </div>
  );
});
